import React, { useState, useEffect, useCallback } from 'react';
// 1. ✅ Import your apiClient, NOT axios
import apiClient from '../apiClient'; // <-- Adjust path as needed

// --- MUI Components ---
import {
  Typography,
  Button,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardMedia,
  CardHeader,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from '@mui/material';

// --- MUI Icons ---
import { Delete as DeleteIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';

// 2. ❌ We no longer need this, the base URL is in apiClient.js
// const API_URL = 'https://api.horizon.co.zm/api/gallery';

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null); // Use pageError for load failures
  const [formError, setFormError] = useState(null); // For upload form errors
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Form State ---
  const [website, setWebsite] = useState('horizonHauliers');
  const [category, setCategory] = useState('');
  const [alt, setAlt] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageName, setImageName] = useState('');

  // --- Dialog State ---
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);

  // --- Snackbar (Toast) State ---
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // --- Data Fetching ---
  const fetchImages = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      // 3. ✅ Use apiClient. The token is added automatically.
      const response = await apiClient.get('/gallery');
      setImages(response.data);
    } catch (err) {
      console.error('Error fetching images:', err);
      // 4. ✅ Better error message
      let errorMsg = 'Failed to fetch images. Please try again later.';
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        errorMsg = 'You are not authorized. Please log out and log in again.';
      }
      setPageError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // --- Form Handlers ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImageName(file.name);
    } else {
      setImageFile(null);
      setImageName('');
    }
  };

  const resetForm = () => {
    setCategory('');
    setAlt('');
    setImageFile(null);
    setImageName('');
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      setFormError('Please select an image file.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    const formData = new FormData();
    formData.append('website', website);
    formData.append('category', category);
    formData.append('alt', alt);
    formData.append('image', imageFile);

    try {
      // 5. ✅ Use apiClient.post. Token is added automatically.
      const response = await apiClient.post('/gallery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImages(prevImages => [response.data, ...prevImages]); // Add to start of list
      resetForm();
      setSnackbar({ open: true, message: 'Image uploaded successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error uploading image:', err);
      // 6. ✅ Better error message for the form
      let errorMsg = 'Failed to upload image. Please try again.';
      if (err.response) {
        if (err.response.status === 401 || err.response.status === 403) {
          errorMsg = 'Authentication failed. Please log in again.';
        } else {
          errorMsg = `Error: ${err.response.data.message || err.message}`;
        }
      }
      setFormError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Delete Handlers ---
  const handleOpenDeleteDialog = (id) => {
    setSelectedImageId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedImageId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!selectedImageId) return;

    try {
      // 7. ✅ Use apiClient.delete. Token is added automatically.
      await apiClient.delete(`/gallery/${selectedImageId}`);
      
      setImages(prevImages => prevImages.filter(img => img._id !== selectedImageId));
      setSnackbar({ open: true, message: 'Image deleted successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error deleting image:', err);
      setSnackbar({ open: true, message: 'Failed to delete image.', severity: 'error' });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // --- Snackbar Close Handler ---
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // --- Render Functions ---

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (pageError) {
      return <Alert severity="error" sx={{ mt: 4 }}>{pageError}</Alert>;
    }

    if (images.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', ...cleanShadow }}>
          <Typography variant="body1">
            No images found in the gallery. Use the form to upload one.
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {images.map(img => (
          <Grid item key={img._id} xs={12} sm={6} md={4}>
            {/* 8. ✅ Apply consistent "white styling" to the Card */}
            <Card sx={{ 
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
              borderRadius: '10px' 
            }}>
              <CardHeader
                title={<Typography variant="body2" fontWeight="500" noWrap>{img.category || 'Uncategorized'}</Typography>}
                subheader={<Typography variant="caption" color="text.secondary">{img.website}</Typography>}
                action={
                  <IconButton
                    aria-label="delete image"
                    onClick={() => handleOpenDeleteDialog(img._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              />
              <CardMedia
                component="img"
                // 9. ✅ Increase height for better visual
                height="200" 
                image={img.imageUrl}
                alt={img.alt || 'Gallery image'}
                sx={{ objectFit: 'cover' }}
              />
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };
  
  // 10. ✅ Define the consistent shadow style
  const cleanShadow = {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
    borderRadius: '10px'
  };

  return (
    // 11. ✅ Use <Box> to fit cleanly in AdminLayout
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Manage Gallery
      </Typography>

      <Grid container spacing={4}>
        {/* --- Upload Form Column --- */}
        <Grid item xs={12} md={4}>
          {/* 12. ✅ Apply consistent "white styling" to the Form Paper */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, ...cleanShadow }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Upload New Image
            </Typography>
            
            {/* 13. ✅ Use the dedicated formError state */}
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="website-select-label">Website</InputLabel>
                    <Select
                      labelId="website-select-label"
                      label="Website"
                      name="website"
                      value={website}
                      onChange={e => setWebsite(e.target.value)}
                    >
                      <MenuItem value="horizonHauliers">Horizon Hauliers</MenuItem>
                      <MenuItem value="horizonEnergies">Horizon Energies</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="category"
                    label="Category (e.g., Italy, Dubai)"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="alt"
                    label="Alt Text (Description)"
                    value={alt}
                    onChange={e => setAlt(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    Select Image
                    <input type="file" hidden onChange={handleFileChange} required accept="image/*" />
                  </Button>
                  {imageName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
                      {imageName}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ py: 1.5 }}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : 'Upload'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* --- Image List Column --- */}
        <Grid item xs={12} md={8}>
          {renderContent()}
        </Grid>
      </Grid>

      {/* --- Delete Confirmation Modal (Unchanged) --- */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Success/Error Snackbar (Unchanged) --- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}